import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Save, RefreshCw, Send, CheckCircle2, XCircle, ExternalLink, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SupportControl() {
  const { getAuthHeaders } = useAuth();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const headers = getAuthHeaders();

  const [settings, setSettings] = useState({
    opener_template_name: 'hello_world',
    opener_template_language: 'en_US',
    auto_fallback_enabled: true,
  });
  const [meta, setMeta] = useState({ phone_number: '', waba_id: '' });
  const [status, setStatus] = useState(null); // live WhatsApp connection health
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/whatsapp/settings`, { headers });
      const d = res.data.data;
      setSettings({
        opener_template_name: d.opener_template_name || 'hello_world',
        opener_template_language: d.opener_template_language || 'en_US',
        auto_fallback_enabled: d.auto_fallback_enabled !== false,
      });
      setMeta({ phone_number: d.phone_number || '', waba_id: d.waba_id || '' });
    } catch (err) {
      toast.error(isAr ? 'فشل تحميل الإعدادات' : 'Failed to load settings');
    }
  };

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/whatsapp/templates`, { headers });
      setTemplates(res.data.data || []);
    } catch (err) {
      toast.error(isAr ? 'فشل تحميل القوالب من Meta' : 'Failed to load templates from Meta');
    }
  };

  const loadStatus = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/whatsapp/status`, { headers });
      setStatus(res.data);
    } catch {
      // non-critical — status card just won't render
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadSettings(), loadTemplates(), loadStatus()]);
      setLoading(false);
    })();
  }, []);

  const selectedTemplate = templates.find(
    (t) =>
      t.name === settings.opener_template_name &&
      t.language === settings.opener_template_language
  );

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/whatsapp/settings`, settings, { headers });
      toast.success(isAr ? 'تم حفظ الإعدادات' : 'Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const sendPreview = async () => {
    if (!testPhone.trim()) {
      toast.error(isAr ? 'أدخل رقم الاختبار' : 'Enter a test phone');
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/whatsapp/templates/preview`,
        null,
        {
          headers,
          params: {
            to: testPhone.replace(/\D/g, ''),
            template_name: settings.opener_template_name,
            language_code: settings.opener_template_language,
          },
        }
      );
      toast.success(
        isAr
          ? `تم الإرسال. Message ID: ${res.data.wa_message_id?.slice(0, 24)}...`
          : `Sent. Message ID: ${res.data.wa_message_id?.slice(0, 24)}...`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الإرسال' : 'Send failed'));
    } finally {
      setTesting(false);
    }
  };

  const renderTemplateBody = (t) => {
    const bodyComp = (t?.components || []).find((c) => c.type === 'BODY');
    return bodyComp?.text || '';
  };

  return (
    <DashboardLayout
      title="التحكم بالدعم"
      titleEn="Support Control"
      subtitle="إدارة القالب الافتتاحي لواتساب"
      subtitleEn="Manage WhatsApp opener template"
      backPath="/dashboard?platform=control"
      breadcrumb={[
        { labelAr: 'التحكم والمعلومات', labelEn: 'Control & Info', path: '/dashboard?platform=control' },
        { labelAr: 'التحكم بالدعم', labelEn: 'Support Control' },
      ]}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* ─── LIVE WHATSAPP CONNECTION HEALTH CARD ───
            Reads /api/whatsapp/status which calls Meta's Graph API using the
            business_management + whatsapp_business_management permissions.
            Displays: verified name, display number, connection state, quality
            rating, platform type.  This is the primary evidence surface used
            by Meta App Review to verify how these permissions are consumed. */}
        {status?.phone && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-emerald-500/[0.08] to-white/[0.02] border border-emerald-500/30 rounded-2xl p-6"
            data-testid="wa-live-status-card"
          >
            <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                    <MessageCircle className="h-6 w-6 text-emerald-400" />
                  </div>
                  <span className="absolute -bottom-0.5 -end-0.5 w-4 h-4 rounded-full bg-emerald-500 border-2 border-[#030303] animate-pulse" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-bold text-lg">
                      {status.phone.verified_name || 'WhatsApp Business'}
                    </h3>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 uppercase tracking-wide">
                      {status.phone.status || 'CONNECTED'}
                    </span>
                  </div>
                  <p className="text-[#71717A] text-sm font-mono" dir="ltr">
                    {status.phone.display_phone_number || '—'}
                  </p>
                </div>
              </div>

              {/* Quality rating pill */}
              {status.phone.quality_rating && (() => {
                const q = String(status.phone.quality_rating).toUpperCase();
                const color =
                  q === 'GREEN'  ? { bg: 'bg-emerald-500/15', tx: 'text-emerald-300', br: 'border-emerald-500/30', dot: 'bg-emerald-500' } :
                  q === 'YELLOW' ? { bg: 'bg-amber-500/15',   tx: 'text-amber-300',   br: 'border-amber-500/30',   dot: 'bg-amber-500'   } :
                  q === 'RED'    ? { bg: 'bg-red-500/15',     tx: 'text-red-300',     br: 'border-red-500/30',     dot: 'bg-red-500'     } :
                                   { bg: 'bg-white/5',        tx: 'text-white/70',    br: 'border-white/10',       dot: 'bg-white/40'    };
                return (
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${color.bg} ${color.br}`}>
                    <span className={`w-2 h-2 rounded-full ${color.dot}`} />
                    <span className={`text-xs font-bold uppercase tracking-wide ${color.tx}`}>
                      {isAr ? 'الجودة' : 'Quality'}: {q}
                    </span>
                  </div>
                );
              })()}
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm pt-4 border-t border-white/5">
              <div>
                <p className="text-[10px] text-[#71717A] uppercase tracking-wide mb-1">
                  {isAr ? 'المنصة' : 'Platform'}
                </p>
                <p className="text-white font-medium">{status.phone.platform_type || 'CLOUD_API'}</p>
              </div>
              <div>
                <p className="text-[10px] text-[#71717A] uppercase tracking-wide mb-1">
                  {isAr ? 'رقم الهاتف ID' : 'Phone Number ID'}
                </p>
                <p className="text-white font-mono text-xs" dir="ltr">{status.phone.id || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] text-[#71717A] uppercase tracking-wide mb-1">
                  WABA ID
                </p>
                <p className="text-white font-mono text-xs" dir="ltr">{meta.waba_id || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] text-[#71717A] uppercase tracking-wide mb-1">
                  {isAr ? 'التكامل' : 'Integration'}
                </p>
                <p className="text-emerald-300 font-medium">Meta Cloud API</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Info banner */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-emerald-500/10 to-white/[0.02] border border-emerald-500/30 rounded-2xl p-5 flex items-start gap-3"
        >
          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <MessageCircle className="h-5 w-5 text-emerald-400" />
          </div>
          <div className="flex-1 text-sm text-[#A1A1AA] leading-relaxed">
            <p className="font-semibold text-white mb-1">
              {isAr ? 'كيف يعمل القالب الافتتاحي؟' : 'How the opener template works'}
            </p>
            <p>
              {isAr
                ? 'قواعد واتساب لا تسمح بإرسال رسائل نصية حرة قبل رد العميل الأول أو خارج نافذة 24 ساعة. عندما يفشل الإرسال بهذا السبب، ترسل المنصة القالب المحدد أدناه تلقائياً لفتح المحادثة.'
                : "WhatsApp doesn't allow freeform text before the customer replies or outside the 24h window. When a send fails for this reason, the platform automatically sends the template selected below to open the chat."}
            </p>
          </div>
        </motion.div>

        {/* Account info card */}
        <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-5">
          <h3 className="text-white font-semibold mb-3">
            {isAr ? 'حساب الأعمال' : 'Business Account'}
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[#71717A] text-xs">{isAr ? 'رقم الهاتف' : 'Phone Number ID'}</p>
              <p className="text-white font-mono" dir="ltr">{meta.phone_number || '—'}</p>
            </div>
            <div>
              <p className="text-[#71717A] text-xs">WABA ID</p>
              <p className="text-white font-mono" dir="ltr">{meta.waba_id || '—'}</p>
            </div>
          </div>
        </div>

        {/* Settings card */}
        <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">
              {isAr ? 'إعدادات القالب' : 'Template Settings'}
            </h3>
            <Button
              size="sm"
              variant="outline"
              onClick={loadTemplates}
              className="border-white/10 text-[#A1A1AA] hover:text-[#d4af37] hover:bg-white/5 hover:border-[#d4af37]/40"
              disabled={loading}
              data-testid="reload-templates-btn"
            >
              <RefreshCw className={`h-4 w-4 me-2 ${loading ? 'animate-spin' : ''}`} />
              {isAr ? 'إعادة تحميل من Meta' : 'Reload from Meta'}
            </Button>
          </div>

          {/* Auto-fallback toggle */}
          <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black/30 p-4">
            <div>
              <p className="text-white font-medium text-sm">
                {isAr ? 'التبديل التلقائي إلى القالب' : 'Auto-fallback to template'}
              </p>
              <p className="text-xs text-[#71717A] mt-1">
                {isAr
                  ? 'عند فشل الرسالة النصية (نافذة 24 ساعة مقفولة)، أرسل القالب المحدد تلقائياً.'
                  : 'When freeform text fails (24h window closed), auto-send the selected template.'}
              </p>
            </div>
            <Switch
              checked={settings.auto_fallback_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, auto_fallback_enabled: v })}
              data-testid="auto-fallback-toggle"
            />
          </div>

          {/* Template picker */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-[#A1A1AA] text-xs mb-2 block">
                {isAr ? 'اختر القالب' : 'Choose template'}
              </Label>
              <Select
                value={`${settings.opener_template_name}::${settings.opener_template_language}`}
                onValueChange={(v) => {
                  const [name, lang] = v.split('::');
                  setSettings({ ...settings, opener_template_name: name, opener_template_language: lang });
                }}
              >
                <SelectTrigger
                  className="bg-white/5 border-white/10 text-white rounded-xl"
                  data-testid="template-select"
                >
                  <SelectValue placeholder={isAr ? 'اختر قالباً' : 'Pick a template'} />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white max-h-72">
                  {templates.length === 0 ? (
                    <div className="p-3 text-xs text-[#71717A]">
                      {isAr ? 'لا توجد قوالب مُعتمدة' : 'No approved templates yet'}
                    </div>
                  ) : (
                    templates.map((t) => (
                      <SelectItem
                        key={`${t.name}::${t.language}`}
                        value={`${t.name}::${t.language}`}
                        className="hover:bg-white/5"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{t.name}</span>
                          <span className="text-xs text-[#71717A]" dir="ltr">({t.language})</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                            {t.category}
                          </span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A1A1AA] text-xs mb-2 block">
                {isAr ? 'الاسم الحالي' : 'Current name'}
              </Label>
              <Input
                value={settings.opener_template_name}
                onChange={(e) => setSettings({ ...settings, opener_template_name: e.target.value })}
                className="bg-white/5 border-white/10 text-white font-mono rounded-xl"
                data-testid="template-name-input"
              />
            </div>
          </div>

          {/* Template preview */}
          {selectedTemplate && (
            <div className="rounded-xl border border-white/10 bg-[#0a0a0a] p-4">
              <p className="text-xs text-[#71717A] mb-2">
                {isAr ? 'معاينة نص القالب' : 'Template body preview'}
              </p>
              <div className="bg-[#25D366]/90 text-white text-sm rounded-2xl rounded-bl-sm p-3 max-w-md whitespace-pre-wrap">
                {renderTemplateBody(selectedTemplate) || (
                  <span className="italic text-white/70">
                    {isAr ? '(لا يوجد نص متن، القالب قد يكون وسائط)' : '(No body text; the template may be media-based)'}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-[#A1A1AA]">
                  {isAr ? 'اللغة' : 'Language'}: <span className="font-mono">{selectedTemplate.language}</span>
                </span>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300">
                  {selectedTemplate.category}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-sky-500/15 border border-sky-500/30 text-sky-300 inline-flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> {selectedTemplate.status}
                </span>
              </div>
            </div>
          )}

          {/* Save button */}
          <div className="flex items-center justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#d4af37] hover:bg-[#e6c353] text-black font-bold shadow-[0_0_20px_rgba(212,175,55,0.25)]"
              data-testid="save-settings-btn"
            >
              <Save className="h-4 w-4 me-2" />
              {saving ? (isAr ? 'جاري الحفظ...' : 'Saving...') : (isAr ? 'حفظ الإعدادات' : 'Save settings')}
            </Button>
          </div>
        </div>

        {/* Test send card */}
        <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-4">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Send className="h-5 w-5 text-[#d4af37]" />
              {isAr ? 'اختبار إرسال القالب' : 'Test send template'}
            </h3>
            <p className="text-sm text-[#71717A] mt-1">
              {isAr
                ? 'أرسل القالب الحالي إلى رقم اختبار لتتأكد أنه يعمل قبل التفعيل.'
                : 'Send the current template to a test number to make sure it works before going live.'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder={isAr ? 'مثال: 971501234567' : 'e.g. 971501234567'}
              className="flex-1 bg-white/5 border-white/10 text-white rounded-xl font-mono"
              dir="ltr"
              data-testid="test-phone-input"
            />
            <Button
              onClick={sendPreview}
              disabled={testing || !testPhone.trim()}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="send-preview-btn"
            >
              <Send className="h-4 w-4 me-2" />
              {testing ? (isAr ? 'جاري...' : 'Sending...') : (isAr ? 'إرسال تجريبي' : 'Send test')}
            </Button>
          </div>
        </div>

        {/* Help card */}
        <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-[#d4af37] shrink-0 mt-0.5" />
            <div className="text-sm text-[#A1A1AA] leading-relaxed">
              <p className="font-semibold text-white mb-1">
                {isAr ? 'كيف تُنشئ قالباً جديداً؟' : 'How to create a new template'}
              </p>
              <ol className="list-decimal ms-4 space-y-1 mt-2">
                <li>
                  {isAr ? 'افتح ' : 'Open '}
                  <a
                    href="https://business.facebook.com/wa/manage/message-templates/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#d4af37] hover:underline inline-flex items-center gap-1"
                  >
                    Meta Business → Message Templates <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
                <li>{isAr ? 'اختر «إنشاء قالب» ثم Utility (خدمة) واختر اللغة.' : 'Click "Create Template", pick Utility, and choose language.'}</li>
                <li>{isAr ? 'اكتب نص القالب باللغة العربية أو الإنجليزية.' : 'Write your template body in Arabic or English.'}</li>
                <li>{isAr ? 'انتظر الموافقة من Meta (عادةً دقائق قليلة).' : 'Wait for Meta approval (usually a few minutes).'}</li>
                <li>{isAr ? 'ارجع هنا واضغط «إعادة تحميل من Meta» واختره من القائمة.' : 'Come back, click "Reload from Meta", and pick it in the list.'}</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Templates listing */}
        <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-5">
          <h3 className="text-white font-semibold mb-3">
            {isAr ? `القوالب المعتمدة (${templates.length})` : `Approved templates (${templates.length})`}
          </h3>
          {templates.length === 0 ? (
            <p className="text-sm text-[#71717A]">
              {isAr ? 'لا توجد قوالب معتمدة بعد.' : 'No approved templates yet.'}
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {templates.map((t) => (
                <div
                  key={`${t.name}-${t.language}`}
                  className={`rounded-xl border p-3 ${
                    t.name === settings.opener_template_name && t.language === settings.opener_template_language
                      ? 'border-[#d4af37]/60 bg-[#d4af37]/[0.06]'
                      : 'border-white/10 bg-black/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-sm text-white">{t.name}</p>
                    <span className="text-xs text-[#71717A]" dir="ltr">{t.language}</span>
                  </div>
                  <p className="text-xs text-[#71717A] mt-1 line-clamp-2">
                    {renderTemplateBody(t) || (isAr ? '(بدون نص)' : '(no body)')}
                  </p>
                  {t.name === settings.opener_template_name && t.language === settings.opener_template_language && (
                    <p className="text-[10px] text-[#d4af37] mt-2 font-semibold">
                      {isAr ? '● القالب المستخدم حالياً' : '● Currently active'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
